/**
 * Cost of Cash Calculator capture form — /cost-of-cash. One form replaces the
 * old inline review form and the "email me the results" button.
 *
 * The calculator's inputs and results arrive as `context` (built by
 * costOfCashContext below, so the keys contextToCrm reads stay in one place)
 * and are rendered in the Lead's Note. Only real, customer-derived numbers are
 * written to CRM fields: values the visitor left at the default are labelled
 * "(default)" in the Note and never sent as CRM data.
 */
import { company, email, fullName, phone } from "./common";

/** Legacy analytics name kept so quote_requested / email_capture reports continue. */

const MONTHLY_CASH = "Monthly cash handled (£)";
const ANNUAL_COST = "Total annual cost";

const gbp = (n) =>
  `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const round2 = (n) => Math.round(Number(n) * 100) / 100;

/**
 * Readable context for the Note. `inputs` are the raw form strings, `defaults`
 * the values the calculator falls back to when an input is blank.
 */
export function costOfCashContext(inputs, results, defaults) {
  if (!results) return {};
  // Mirrors the calculator: a blank or zero input falls back to the default.
  const entered = (key) => parseFloat(inputs[key]) || null;
  const withDefault = (key, format) => {
    const value = entered(key);
    return value ? format(value) : `${format(defaults[key])} (default)`;
  };
  return {
    "Weekly cash takings (£)": entered("weeklyCash"),
    "Bank trips per week": entered("bankTrips"),
    "Time per bank trip": withDefault("timePerTrip", (v) => `${v} mins`),
    "Staff hourly wage": withDefault("hourlyWage", gbp),
    "Cash counting time per day": withDefault("countingMinutes", (v) => `${v} mins`),
    "Days open per week": withDefault("daysOpen", (v) => `${v}`),
    "Bank deposit fee": withDefault("depositFee", (v) => `${v}%`),
    "Estimated cash losses": withDefault("cashLoss", (v) => `${v}%`),
    [MONTHLY_CASH]: round2(results.monthlyCash),
    "Monthly labour cost": gbp(results.labourCost),
    "Monthly banking fees": gbp(results.bankingCost),
    "Monthly cash loss": gbp(results.cashLoss),
    "Hours per month on cash handling": `${round2(results.totalHours)}`,
    "Total monthly cost": gbp(results.monthlyCost),
    [ANNUAL_COST]: gbp(results.annualCost),
  };
}

const toolCostOfCash = {
  id: "tool_cost_of_cash",
  label: "Cost of Cash Calculator",
  crm: { layout: "Standard", leadSource: "Website Calculator - Cost of Cash" },
  fields: [
    [fullName, company],
    [email, { ...phone, help: "We'll only call to discuss your results — never spam." }],
  ],
  submitLabel: "Send my review",
  consent: { variant: "standard" },
  success: { title: "Thank you", body: "We'll be in touch with your personalised review shortly." },
  contextToCrm: (ctx) => ({
    Monthly_Turnover: typeof ctx[MONTHLY_CASH] === "number" ? ctx[MONTHLY_CASH] : null,
    Needs: ["Card machine"],
  }),
  summary: (values, ctx) =>
    ctx[ANNUAL_COST] ? `Cost of Cash Calculator: cash costing ${ctx[ANNUAL_COST]} a year` : "Cost of Cash Calculator",
};

export default toolCostOfCash;
